import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Car, LogOut, ShieldCheck, Store, Menu, Search, Link2, Sparkles, LayoutGrid, UserCircle, Sun, Moon, Tag, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetClose, SheetTitle } from "@/components/ui/sheet";
import { NotificationBell } from "@/components/NotificationBell";
import { useContent, SITE_DEFAULTS } from "@/lib/content";

export function Navbar() {
  const { user, logout } = useAuth();
  const c = useContent("site", SITE_DEFAULTS);
  const { pathname } = useLocation();
  const isStaff = user && ["super_admin", "broker", "sales", "staff", "admin"].includes(user.role);
  const [unreadMsgs, setUnreadMsgs] = useState(0);
  useEffect(() => {
    if (!isStaff) return;
    const load = () => api.get("/contact/messages/unread-count").then(({ data }) => setUnreadMsgs(data.count || 0)).catch(() => {});
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [isStaff, pathname]);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [dark, setDark] = useState(() => typeof document !== "undefined" && document.documentElement.classList.contains("dark"));
  const toggleTheme = () => {
    const el = document.documentElement;
    const next = !el.classList.contains("dark");
    el.classList.toggle("dark", next);
    localStorage.setItem("ab-theme", next ? "dark" : "light");
    setDark(next);
  };
  const isManager = user && ["super_admin", "broker", "admin"].includes(user.role);
  const isPartner = user && user.role === "partner";
  const home = isPartner ? "/partner" : isStaff ? "/staff" : user ? "/dashboard" : "/";

  const buyerLinks = [
    { to: "/inventory", label: c.nav_find_car, testId: "nav-find-car", icon: Search },
    { to: "/request/new", label: c.nav_paste_link, testId: "nav-paste-link", icon: Link2 },
    { to: "/concierge", label: c.nav_dream_car, testId: "nav-concierge", icon: Sparkles },
    { to: "/specials", label: c.nav_specials, testId: "nav-specials", icon: Tag },
    { to: "/contact", label: c.nav_contact, testId: "nav-contact", icon: Mail },
  ];
  const staffLinks = [
    { to: "/staff", label: "Dashboard", testId: "nav-staff-dashboard" },
    { to: "/staff/messages", label: "Messages", testId: "nav-staff-messages" },
    { to: "/staff/prequal", label: "Prequal", testId: "nav-staff-prequal" },
    { to: "/staff/inventory", label: "Inventory", testId: "nav-staff-inventory" },
    { to: "/staff/duplicates", label: "Duplicates", testId: "nav-staff-duplicates" },
    { to: "/staff/partners", label: "Partners", testId: "nav-staff-partners" },
    ...(isManager ? [{ to: "/staff/team", label: "Team", testId: "nav-staff-team" }, { to: "/staff/content", label: "Content", testId: "nav-staff-content" }, { to: "/staff/promotions", label: "Specials", testId: "nav-staff-promotions" }] : []),
  ];
  const partnerLinks = [
    { to: "/partner", label: "My Inventory", testId: "nav-partner-inventory" },
    { to: "/partner/analytics", label: "Analytics", testId: "nav-partner-analytics" },
    { to: "/partner/specials", label: "My Specials", testId: "nav-partner-specials" },
    { to: "/partner/profile", label: "Dealer profile", testId: "nav-partner-profile" },
  ];
  const links = isStaff ? staffLinks : isPartner ? partnerLinks
    : [...buyerLinks, ...(user ? [{ to: "/dashboard", label: c.nav_my_garage, testId: "nav-my-garage", icon: LayoutGrid }] : [])];

  const NavLink = ({ to, children, testId, badge }) => (
    <Link to={to} data-testid={testId}
      className={`relative px-3.5 py-2 rounded-md text-sm font-semibold transition-all duration-150 ${pathname === to ? "text-white bg-slate-800/80" : "text-slate-300 hover:text-white hover:bg-slate-800/60"}`}>
      {children}
      {badge > 0 && <span data-testid={`${testId}-badge`} className="ml-1.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-rose-600 px-1 align-middle text-[10px] font-bold leading-none text-white">{badge > 99 ? "99+" : badge}</span>}
      {pathname === to && <span className="absolute inset-x-3 -bottom-[1px] h-0.5 rounded-full bg-rose-500" />}
    </Link>
  );

  return (
    <header className="sticky top-0 z-50 w-full border-b border-slate-800 bg-slate-950/95 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
        <Link to={home} className="group flex items-center gap-2.5" data-testid="nav-logo">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-gradient-to-br from-rose-600 to-rose-700 text-white shadow-sm transition-transform group-hover:scale-105"><Car className="h-5 w-5" strokeWidth={2.5} /></div>
          <span className="font-display text-xl font-black uppercase tracking-tight text-white">AutoBroker</span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex">
          {links.map((l) => <NavLink key={l.to} to={l.to} testId={l.testId} badge={l.to === "/staff/messages" ? unreadMsgs : 0}>{l.label}</NavLink>)}
        </nav>

        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={toggleTheme} data-testid="theme-toggle" aria-label="Toggle dark mode" className="max-sm:hidden text-slate-300 hover:bg-slate-800 hover:text-white">
            {dark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </Button>
          {user ? (
            <>
              {!isPartner && <NotificationBell />}
              <div className="hidden items-center gap-2 text-sm sm:flex">
                {isStaff && <ShieldCheck className="h-4 w-4 text-rose-500" />}
                {isPartner && <Store className="h-4 w-4 text-indigo-400" />}
                {!isStaff && !isPartner ? (
                  <Link to="/profile" data-testid="nav-profile" className="font-semibold text-slate-200 hover:text-white">
                    {user.name || user.email}
                    {!user.legal_name && <span className="ml-1 inline-block h-2 w-2 rounded-full bg-amber-500 align-middle" title="Complete your profile" data-testid="nav-profile-dot" />}
                  </Link>
                ) : (
                  <span className="font-semibold text-slate-200">{user.name || user.email}</span>
                )}
              </div>
              <Button variant="ghost" size="sm" onClick={() => { logout(); navigate("/"); }} data-testid="nav-logout" className="text-slate-300 hover:bg-slate-800 hover:text-white"><LogOut className="h-4 w-4" /></Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => navigate("/login")} data-testid="nav-login" className="hidden font-semibold text-slate-200 hover:bg-slate-800 hover:text-white sm:inline-flex">{c.nav_sign_in}</Button>
              <Button onClick={() => navigate("/concierge")} data-testid="nav-get-started" className="rounded-md bg-rose-600 font-bold text-white shadow-sm shadow-rose-600/20 hover:bg-rose-700">{c.nav_get_started}</Button>
            </>
          )}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="sm" className="text-slate-200 hover:bg-slate-800 hover:text-white md:hidden" data-testid="mobile-menu-toggle-button"><Menu className="h-5 w-5" /></Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80" aria-describedby={undefined}>
              <SheetTitle className="sr-only">Menu</SheetTitle>
              <div className="mt-8 flex flex-col gap-1">
                {links.map((l) => (
                  <SheetClose asChild key={l.to}>
                    <Link to={l.to} data-testid={`m-${l.testId}`} className="flex items-center gap-3 rounded-md px-3 py-3 text-base font-semibold text-slate-700 dark:text-slate-300 transition-colors hover:bg-slate-100">
                      {l.icon ? <l.icon className="h-5 w-5 text-rose-600" /> : null}{l.label}
                    </Link>
                  </SheetClose>
                ))}
              </div>
              <div className="mt-6 border-t border-slate-200 dark:border-slate-800 pt-6">
                {user ? (
                  <div className="flex flex-col gap-2">
                    {!isStaff && !isPartner && (
                      <SheetClose asChild><Link to="/profile" data-testid="m-nav-profile" className="flex items-center gap-3 rounded-md px-3 py-3 text-base font-semibold text-slate-700 dark:text-slate-300 transition-colors hover:bg-slate-100"><UserCircle className="h-5 w-5 text-rose-600" /> My Profile</Link></SheetClose>
                    )}
                    <Button variant="outline" onClick={() => { setOpen(false); logout(); navigate("/"); }} className="w-full rounded-md font-semibold" data-testid="m-logout">Sign out</Button>
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                    <SheetClose asChild><Link to="/concierge"><Button className="w-full rounded-md bg-rose-600 font-bold text-white hover:bg-rose-700" data-testid="m-get-started">{c.nav_get_started}</Button></Link></SheetClose>
                    <SheetClose asChild><Link to="/login"><Button variant="outline" className="w-full rounded-md font-semibold" data-testid="m-login">{c.nav_sign_in}</Button></Link></SheetClose>
                  </div>
                )}
                <p className="mt-4 text-xs text-slate-400 dark:text-slate-500">Licensed California vehicle brokerage · Serving San Diego to Redding.</p>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
